import { useQuery } from '@tanstack/react-query';
import type { Database } from '@trade-connector/shared';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type ManufacturerClientRow = Database['public']['Tables']['manufacturers']['Row'];

export interface UseManufacturerForClientResult {
    manufacturer: ManufacturerClientRow | null;
    enabled: boolean;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<unknown>;
}

/**
 * Fetches the `manufacturers` row linked to the signed-in client user's `customer_id`
 * and checks `rpc('manufacturer_service_enabled')`.
 */
export function useManufacturerForClient(): UseManufacturerForClientResult {
    const { user } = useAuth();
    const customerId = user?.customer_id ?? null;

    const query = useQuery({
        queryKey: ['fleet', 'manufacturer-for-client', customerId],
        queryFn: async (): Promise<{
            manufacturer: ManufacturerClientRow | null;
            enabled: boolean;
        }> => {
            if (!customerId) {
                return { manufacturer: null, enabled: false };
            }

            const { data: mfr, error: mfrErr } = await supabase
                .from('manufacturers')
                .select('*')
                .eq('customer_id', customerId)
                .maybeSingle();

            if (mfrErr) throw mfrErr;
            if (!mfr) {
                return { manufacturer: null, enabled: false };
            }

            const { data: flagEnabled, error: flagErr } = await supabase.rpc(
                'manufacturer_service_enabled',
                { p_manufacturer_id: mfr.id },
            );

            if (flagErr) throw flagErr;

            return {
                manufacturer: mfr as ManufacturerClientRow,
                enabled: Boolean(flagEnabled),
            };
        },
        enabled: Boolean(customerId),
        staleTime: 60_000,
    });

    return {
        manufacturer: query.data?.manufacturer ?? null,
        enabled: Boolean(query.data?.manufacturer && query.data?.enabled),
        isLoading: Boolean(customerId) && query.isLoading,
        error: (query.error as Error | null) ?? null,
        refetch: query.refetch,
    };
}
